import { forwardRef, type TextareaHTMLAttributes } from "react";

/**
 * Shared textarea component — matches Input styling for form consistency.
 */

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, className = "", id, ...props }, ref) => {
    const hasError = Boolean(error);

    return (
      <div className="w-full">
        {label && id && (
          <label
            htmlFor={id}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={id}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError && id ? `${id}-error` : undefined}
          className={[
            "w-full px-3 py-2 text-sm rounded-lg border bg-white dark:bg-gray-900",
            "text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500",
            "focus:outline-none focus:ring-2 focus:ring-offset-0",
            "transition-colors duration-150 resize-y min-h-[80px]",
            hasError
              ? "border-red-400 dark:border-red-600 focus:ring-red-500/40"
              : "border-gray-200 dark:border-gray-700 focus:ring-blue-500/40 focus:border-blue-500 dark:focus:border-blue-400",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            className,
          ].join(" ")}
          {...props}
        />
        {hasError && id ? (
          <p id={`${id}-error`} role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">
            {error}
          </p>
        ) : hint ? (
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{hint}</p>
        ) : null}
      </div>
    );
  }
);

Textarea.displayName = "Textarea";

export default Textarea;
